import React, { useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { map, prop, reverse, slice, sortBy } from "ramda";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";

import { notBlank } from "../../util/ext";
import { TableFooter } from "@material-ui/core";
import { DateTime } from "luxon";
import Tooltip from "@material-ui/core/Tooltip";
import { Link, useRouteMatch } from "react-router-dom";

const ROWS_PER_PAGE = 15;

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  link: {
    color: theme.palette.primary.brandPrimary,
  },

  tableContainer: {
    height: "calc(100vh - 350px)",
    overflow: "auto",
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },
}));

const ContentIngestionJobTable = props => {
  const {
    metadataFileDiscoveryList,
    page,
    onPageChange,
    onScrollVisible,
  } = props;

  const classes = useStyles();
  const { url } = useRouteMatch();

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer className={classes.tableContainer} ref={tableContainerEl}>
        <Table stickyHeader aria-label="Metadata File Discovery">
          <TableHead>
            <TableRow>
              <TableCell
                key="eventTimestamp"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Timestamp
              </TableCell>

              <TableCell
                key="jobId"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Job Id
              </TableCell>

              <TableCell
                key="metadataFileLocation"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Metadata File Location
              </TableCell>

              <TableCell
                key="jobStatus"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Status
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {map(
              mfd => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={
                    mfd.jobId + DateTime.fromISO(mfd.eventTimestamp).toMillis()
                  }
                >
                  <TableCell
                    key={"eventTimestamp:" + mfd.jobId}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {DateTime.fromISO(mfd.eventTimestamp)
                      .toUTC()
                      .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                  </TableCell>

                  <TableCell
                    key={"jobId:" + mfd.jobId}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    <Tooltip title="View Event">
                      <Link className={classes.link} to={`${url}/${mfd.jobId}`}>
                        {mfd.jobId}
                      </Link>
                    </Tooltip>
                  </TableCell>

                  <TableCell
                    key={"metadataFileLocation:" + mfd.jobId}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {mfd.filename}
                  </TableCell>

                  <TableCell
                    key={"jobStatus:" + mfd.jobId}
                    align="left"
                    style={{ minWidth: 75, textTransform: "capitalize" }}
                  >
                    {mfd.jobStatus}
                  </TableCell>
                </TableRow>
              ),
              slice(
                page * ROWS_PER_PAGE,
                page * ROWS_PER_PAGE + ROWS_PER_PAGE,
                reverse(
                  sortBy(prop("eventTimestamp"), metadataFileDiscoveryList),
                ),
              ),
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                classes={{
                  root: classes.pagination,
                }}
                colSpan={4}
                count={metadataFileDiscoveryList.length}
                rowsPerPageOptions={[ROWS_PER_PAGE]}
                rowsPerPage={ROWS_PER_PAGE}
                page={page}
                onChangePage={onPageChange}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ContentIngestionJobTable;
